import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ServiceNotification } from '@shared/services/notification/service-notification';

export type PdfGenerationStatus = 'idle' | 'running' | 'done' | 'error' | 'cancelled';

export interface PdfGenerationState {
	status: PdfGenerationStatus;
	progress: number;
	phaseLabel: string;
	lineLabel?: string;
	errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class PdfGenerationManagerService {
	private readonly toastKey = 'pdf-generation';

	private cancelled = false;

	private readonly stateSubject = new BehaviorSubject<PdfGenerationState>({
		status: 'idle',
		progress: 0,
		phaseLabel: '',
	});
	readonly state$ = this.stateSubject.asObservable();

	constructor(private notification: ServiceNotification) {}

	getState(): PdfGenerationState {
		return this.stateSubject.value;
	}

	isBusy(): boolean {
		return this.getState().status === 'running';
	}

	isCancelled(): boolean {
		return this.cancelled;
	}

	start(lineLabel: string): void {
		if (this.isBusy()) return;

		this.cancelled = false;
		this.setState({
			status: 'running',
			progress: 0,
			phaseLabel: 'Iniciando…',
			lineLabel,
			errorMessage: undefined,
		});
		this.showToast();
	}

	update(progress: number, phaseLabel: string): void {
		if (!this.isBusy()) return;
		this.setState({
			progress: Math.max(0, Math.min(100, Math.round(progress))),
			phaseLabel,
		});
	}

	done(message = 'PDF gerado e baixado.'): void {
		this.setState({ status: 'done', progress: 100, phaseLabel: 'Concluído' });
		this.notification.success('Sucesso', message);
		setTimeout(() => this.reset(), 2000);
	}

	fail(err: any): void {
		const msg =
			err?.message ||
			err?.error?.message ||
			(typeof err?.error === 'string' ? err.error : null) ||
			'Não foi possível gerar o PDF.';
		this.setState({ status: 'error', errorMessage: msg, phaseLabel: 'Erro' });
		this.notification.error('Erro', msg);
		setTimeout(() => this.clearToast(), 3000);
	}

	cancel(): void {
		if (!this.isBusy()) return;
		this.cancelled = true;
		this.setState({ status: 'cancelled', progress: 0, phaseLabel: '' });
		this.notification.warn('Cancelado', 'Geração do PDF cancelada.');
		this.clearToast();
	}

	private reset(): void {
		this.clearToast();
		this.cancelled = false;
		this.setState({
			status: 'idle',
			progress: 0,
			phaseLabel: '',
			lineLabel: undefined,
			errorMessage: undefined,
		});
	}

	private setState(patch: Partial<PdfGenerationState>): void {
		this.stateSubject.next({ ...this.stateSubject.value, ...patch });
	}

	private showToast(): void {
		this.notification.showProgress(this.toastKey, 'Gerando PDF', {
			data: { type: 'pdf-generation-progress' },
		});
	}

	private clearToast(): void {
		this.notification.clearProgress(this.toastKey);
	}
}

